/** Shot put circle and landing sector on the infield, plus the shot itself. */
import * as THREE from 'three';
import { SHOT_PUT } from '@decathlon/shared';
import { box, mat } from '../blocky.js';

export const SP_Z = -41;
/** The athlete's mark: the centre of the circle. Distances are measured from the stop board. */
export const SP_X = 0;
const LINE = '#f5f5f5';
const CIRCLE_SURFACE_Y = 0.07;
const RUNWAY_SURFACE_Y = 0.1;

export class ShotPutArea {
  readonly group = new THREE.Group();
  private shot: THREE.Mesh;
  private discipline = 'sp';
  private extras = new THREE.Group();
  private flag: THREE.Group;
  private carryDirection = new THREE.Vector3();
  private implementAxis = new THREE.Vector3(0, 1, 0);

  constructor() {
    const g = this.group;
    g.add(this.extras);
    // Concrete circle (2.135 m across) with a stop board at the front.
    const circle = new THREE.Mesh(new THREE.CylinderGeometry(1.07, 1.07, 0.1, 24), mat('#b0bec5'));
    circle.position.set(SP_X, CIRCLE_SURFACE_Y - 0.05, SP_Z);
    circle.receiveShadow = true;
    g.add(circle);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.1, 0.04, 6, 32), mat(LINE));
    ring.rotation.x = Math.PI / 2;
    ring.position.set(SP_X, 0.07, SP_Z);
    g.add(ring);
    g.add(box(0.12, 0.1, 1.2, LINE, SP_X + SHOT_PUT.boardOffset, 0.1, SP_Z, 0.02)); // stop board
    // Landing sector: two lines from the circle, 34.92° apart, and distance arcs.
    const half = (34.92 / 2) * (Math.PI / 180);
    for (const side of [-1, 1]) {
      const len = 24;
      const line = box(len, 0.02, 0.08, LINE, 0, 0.06, 0, 0);
      line.castShadow = false;
      line.position.set(
        SP_X + (len / 2) * Math.cos(half),
        0.06,
        SP_Z + side * (len / 2) * Math.sin(half),
      );
      line.rotation.y = -side * half;
      g.add(line);
    }
    for (const d of [10, 15, 20]) {
      const arc = new THREE.Mesh(
        new THREE.RingGeometry(
          d - 0.03 + SHOT_PUT.boardOffset,
          d + 0.03 + SHOT_PUT.boardOffset,
          32,
          1,
          -half + Math.PI / 2 - Math.PI / 2,
          half * 2,
        ),
        mat(d === 15 ? '#1e88e5' : LINE),
      );
      arc.rotation.x = -Math.PI / 2;
      arc.rotation.z = -Math.PI / 2 - half + Math.PI / 2 + half; // ring geometry starts on +x in its own plane
      arc.position.set(SP_X, 0.05, SP_Z);
      g.add(arc);
    }
    // Oversized for legibility: a real shot is 12 cm across and vanishes at this camera distance.
    this.shot = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 8), mat('#37474f'));
    this.shot.name = 'throw-implement';
    this.shot.castShadow = true;
    this.shot.visible = false;
    g.add(this.shot);
    this.flag = new THREE.Group();
    this.flag.add(box(0.06, 1.2, 0.06, LINE, 0, 0.6, 0, 0.01));
    this.flag.add(box(0.5, 0.3, 0.04, '#e53935', 0.25, 1.05, 0, 0.01));
    this.flag.visible = false;
    g.add(this.flag);
  }

  configure(event: string): void {
    this.discipline = event;
    this.extras.clear();
    this.shot.geometry.dispose();
    this.shot.geometry =
      event === 'jt'
        ? new THREE.CylinderGeometry(0, 0.035, 2.6, 8).translate(0, -1.3, 0)
        : event === 'dt'
          ? new THREE.CylinderGeometry(0.24, 0.24, 0.06, 16)
          : new THREE.SphereGeometry(0.16, 12, 8);
    this.shot.rotation.set(0, 0, event === 'jt' ? -Math.PI / 2 : 0);
    if (event === 'jt') {
      this.extras.add(box(34, 0.04, 2, '#c1493f', 13, RUNWAY_SURFACE_Y - 0.02, SP_Z, 0));
      this.extras.add(box(0.12, 0.05, 2.2, LINE, 30, 0.12, SP_Z, 0));
    }
    if (event === 'dt') {
      for (const z of [-2.5, 2.5])
        for (const x of [-2, 0, 2])
          this.extras.add(box(0.1, 5, 0.1, '#607d8b', x, 2.5, SP_Z + z, 0));
      for (const z of [-2.5, 2.5])
        for (let y = 0.5; y < 5; y += 0.5)
          this.extras.add(box(4, 0.035, 0.035, '#90a4ae', 0, y, SP_Z + z, 0));
    }
    if (event === 'dt' || event === 'jt')
      for (const side of [-1, 1]) {
        const angle = ((event === 'jt' ? 14.5 : 17.46) * Math.PI) / 180;
        const line = box(
          75,
          0.02,
          0.08,
          LINE,
          (event === 'jt' ? 30 : 0) + 37.5 * Math.cos(angle),
          0.06,
          SP_Z + side * 37.5 * Math.sin(angle),
          0,
        );
        line.rotation.y = -side * angle;
        this.extras.add(line);
      }
  }

  get surfaceHeight(): number {
    return this.discipline === 'jt' ? RUNWAY_SURFACE_Y : CIRCLE_SURFACE_Y;
  }

  /** The shaft's centre stays in the throwing hand; the tip points down the runway. */
  carryJavelin(hand: THREE.Vector3, yaw: number): void {
    if (this.discipline !== 'jt') return;
    const pitch = 0.08;
    this.carryDirection.set(
      Math.cos(yaw) * Math.cos(pitch),
      Math.sin(pitch),
      -Math.sin(yaw) * Math.cos(pitch),
    );
    this.shot.visible = true;
    this.shot.quaternion.setFromUnitVectors(this.implementAxis, this.carryDirection);
    this.shot.position.copy(hand).addScaledVector(this.carryDirection, 1.3);
  }

  /** Javelin position is its tip; other implements are centred above the ground. */
  setShot(pos: { x: number; y: number; pitch?: number } | null): void {
    this.shot.visible = pos !== null;
    if (!pos) return;
    if (this.discipline === 'jt') {
      // The local +Y tip is at the origin, so impact plants the nose rather than
      // burying half the shaft. Keep the impact pitch while the result is shown.
      this.shot.rotation.set(0, 0, (pos.pitch ?? 0) - Math.PI / 2);
      this.shot.position.set(SP_X + pos.x, pos.y, SP_Z);
    } else this.shot.position.set(SP_X + pos.x, pos.y + 0.16, SP_Z);
  }

  /** Plant the flag where the shot landed (distance from the stop board). */
  mark(dist: number | null): void {
    this.flag.visible = dist !== null;
    if (dist !== null)
      this.flag.position.set(
        SP_X +
          (this.discipline === 'jt' ? 30 : this.discipline === 'dt' ? 0 : SHOT_PUT.boardOffset) +
          dist,
        0,
        SP_Z - 1.2,
      );
  }
}
